import { ChangeEventHandler, FC, MutableRefObject, PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import Webcam from 'react-webcam';

import { CAPTURE_VIDEO_CONSTRAINTS } from '../capture-constraints';

import { Dialog } from 'components';
import { ImageOverlay } from 'utils';

import { ActiveUnit, Chassis, EquipmentType } from 'types';
import { TireDamage, TireDamageType, useTireInspections } from '.';
import '../damages/damage-image.scss';
import '../damages/damages.scss';
import { useNewTransaction } from '../new-transaction.context';

const TireDialogWrapper: FC<
  PropsWithChildren<{ className?: string; back?: () => void; next?: () => void }>
> = props => {
  return (
    <div>
      <div className="d-flex bg-primary g-3 p-1">
        <button type="button" className="btn-back d-flex g-2" onClick={props.back} disabled={!props.back}>
          <span className="material-icons icon-large">arrow_back</span>
          <span>BACK</span>
        </button>
        <h4 className="flex-grow-1 text-center">TIRE INSPECTION</h4>
        <button type="button" className="btn-back d-flex g-2" onClick={props.next} disabled={!props.next}>
          <span>NEXT</span>
          <span className="material-icons icon-large">arrow_forward</span>
        </button>
      </div>
      <div className={`dialog-content ${props.className ?? ''} p-5`}>{props.children}</div>
    </div>
  );
};

const TireImage: FC = () => {
  const { capture, currentTire, setCapture, setCurrentTire } = useTireInspections();
  const { container, chassis, genset, stack, bulk } = useNewTransaction();
  const webcamRef = useRef<Webcam>(null);

  /* prettier-ignore */
  const setImage = useCallback((imageBase64?: string) => {
    setCurrentTire(prevState => ({ ...prevState, imageBase64 }));
  }, [setCurrentTire]);

  const handleCapture = useCallback(async () => {
    const text = bulk
      ? (chassis.unitNumber ?? '') +
        ' ' +
        (stack as ActiveUnit[])
          .filter((u): u is Chassis => u.equipmentType !== EquipmentType.GENSET)
          .map(u => u.unitNumber ?? '')
          .slice(0, 3)
          .join(' ')
      : [container.unitNumber ?? '', chassis.unitNumber ?? ' ', genset.unitNumber ?? ' '].join(' ');
    ImageOverlay.withText(text + ' ' + new Date().toLocaleString())
      .withSetter(setImage)
      .call(Webcam.prototype.getScreenshot.call(webcamRef.current));
  }, [setImage, bulk, stack, container.unitNumber, chassis.unitNumber, genset.unitNumber]);
  return !capture || !currentTire ? null : (
    <TireDialogWrapper
      className="webcam"
      back={() => setCapture(false)}
      next={!currentTire.imageBase64 ? undefined : () => setCapture(false)}
    >
      {!currentTire.imageBase64 ? (
        <div className="d-flex flex-column">
          <Webcam
            width="100%"
            ref={webcamRef}
            videoConstraints={CAPTURE_VIDEO_CONSTRAINTS}
            screenshotFormat="image/jpeg"
          />
          <button type="button" className="btn-close" onClick={handleCapture}>
            <span className="material-icons">circle</span>
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column">
          <img alt="preview" width="100%" src={currentTire.imageBase64} />
          <button type="button" className="btn-close" onClick={() => setImage(undefined)}>
            <span className="material-icons">rotate_left</span>
          </button>
        </div>
      )}
    </TireDialogWrapper>
  );
};

const TireInspection: FC = () => {
  const { currentTire, nextTireKey, prevTireKey, tireKey, selectTire, setCapture, setCurrentTire } =
    useTireInspections();

  let measurement0Ref: MutableRefObject<HTMLSelectElement | null> = useRef(null);
  let measurement1Ref: MutableRefObject<HTMLSelectElement | null> = useRef(null);
  let measurement2Ref: MutableRefObject<HTMLSelectElement | null> = useRef(null);

  const damageType: TireDamageType | undefined = useMemo(() => currentTire?.damageType, [currentTire?.damageType]);
  const measurement0: number | undefined = useMemo(() => currentTire?.measurement0, [currentTire?.measurement0]);
  const measurement1: number | undefined = useMemo(() => currentTire?.measurement1, [currentTire?.measurement1]);
  const measurement2: number | undefined = useMemo(() => currentTire?.measurement2, [currentTire?.measurement2]);

  /* prettier-ignore */
  const damageTypeOptions: JSX.Element[] = useMemo(() => {
    return Object.values(TireDamage).map(damage => (<option key={damage} value={damage}>{damage}</option>));
  }, []);

  /* prettier-ignore */
  const measurementOptions: JSX.Element[] = useMemo(() => {
    return Array(20).fill(0).map((_, num) => <option key={num} value={num}>{num} / 32"</option>);
  }, []);

  /* prettier-ignore */
  const handleChangeDamageType: ChangeEventHandler<HTMLSelectElement> = useCallback(e => {
    const newDamageType = Object.values(TireDamage).find(td => td === e.target.value);
    setCurrentTire(prevState => ({
      ...prevState,
      damageType: newDamageType,
      measurement1: newDamageType === TireDamage.SLID_FLAT ? prevState.measurement1 : undefined,
      measurement2: newDamageType === TireDamage.SLID_FLAT ? prevState.measurement2 : undefined
    }));
  }, [setCurrentTire]);

  /* prettier-ignore */
  const handleChangeMeasurement: ChangeEventHandler<HTMLSelectElement> = useCallback(e => {
    const newMeasurement = isNaN(Number(e.target.value)) ? undefined : Number(e.target.value);
    setCurrentTire(prevState => ({
      ...prevState,
      measurement0: e.target.name === 'measurement0' ? newMeasurement : prevState.measurement0,
      measurement1: e.target.name === 'measurement1' ? newMeasurement : prevState.measurement1,
      measurement2: e.target.name === 'measurement2' ? newMeasurement : prevState.measurement2
    }));
  }, [setCurrentTire]);

  const handleNext = useCallback(() => {
    switch (damageType) {
      case TireDamage.SLID_FLAT:
        if (measurement0 === undefined) {
          return measurement0Ref.current?.reportValidity();
        } else if (measurement1 === undefined) {
          return measurement1Ref.current?.reportValidity();
        } else if (measurement2 === undefined) {
          return measurement2Ref.current?.reportValidity();
        } else break;
      case TireDamage.WORN:
        if (measurement0 === undefined) {
          return measurement0Ref.current?.reportValidity();
        } else break;
    }
    selectTire(nextTireKey);
  }, [damageType, measurement0, measurement1, measurement2, nextTireKey, selectTire]);

  return !currentTire ? null : (
    <TireDialogWrapper back={() => selectTire(prevTireKey)} next={handleNext}>
      <div className="d-flex justify-content-center">
        <div className="grid-1 max-width-fit">
          <h5 className="mb-3 text-center text-primary">
            {currentTire.axle} | {currentTire.side} | {currentTire.centrality === 'INNER' ? 'INSIDE' : 'OUTSIDE'}
          </h5>
          <div className="grid-5">
            {Array(20)
              .fill(0)
              .map((_, num) => (
                <button
                  key={num}
                  type="button"
                  className="btn-outline justify-content-center text-info-main g-2 h-100 w-100"
                  onClick={() => setCurrentTire(prevState => ({ ...prevState, measurement0: num }))}
                  ref={node => node?.toggleAttribute('selected', num === measurement0)}
                >
                  <label className="font-weight-bold white-space-nowrap">{num} / 32"</label>
                </button>
              ))}
          </div>
          <h6 className="my-3 text-center text-primary">DAMAGES</h6>
          <div className="grid-4 auto-rows w-100">
            <div className="damage-input col-span-3">
              <select
                className="w-100"
                name="damageType"
                id={`${tireKey}-damage-type`}
                value={damageType ?? ''}
                onChange={handleChangeDamageType}
                ref={node => node?.toggleAttribute('blank', !currentTire.damageType)}
              >
                <option key="" value="" />
                {damageTypeOptions}
              </select>
              <label htmlFor={`${tireKey}-damage-type`}>DAMAGE TYPE</label>
            </div>
            <div className="row-span-2 h-100 w-100">
              {!currentTire.imageBase64 ? (
                <button
                  type="button"
                  className="btn-outline justify-content-center h-100 w-100 grid-1 text-primary"
                  onClick={() => setCapture(true)}
                >
                  <span className="material-icons image">camera_alt</span>
                  <span className="white-space-nowrap text-base">TAKE PHOTO</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-outline justify-content-center text-info-main h-100 w-100 grid-1"
                  onClick={() => setCapture(true)}
                >
                  <span className="material-icons image">image</span>
                  <span className="white-space-nowrap text-base">VIEW PHOTO</span>
                </button>
              )}
            </div>
            <div className="damage-input">
              <select
                className="w-100"
                name="measurement0"
                id={`${tireKey}-measurement-0`}
                value={measurement0 ?? ''}
                onChange={handleChangeMeasurement}
                ref={node => {
                  node?.toggleAttribute('blank', measurement0 === undefined);
                  measurement0Ref.current = node;
                }}
                disabled={damageType !== TireDamage.SLID_FLAT && damageType !== TireDamage.WORN}
                required={damageType === TireDamage.SLID_FLAT || damageType === TireDamage.WORN}
              >
                <option key="" value="" />
                {measurementOptions}
              </select>
              <label htmlFor={`${tireKey}-measurement-0`}>MEAS. 1</label>
            </div>
            <div className="damage-input">
              <select
                className="w-100"
                name="measurement1"
                id={`${tireKey}-measurement-1`}
                value={measurement1 ?? ''}
                onChange={handleChangeMeasurement}
                ref={node => {
                  node?.toggleAttribute('blank', measurement1 === undefined);
                  measurement1Ref.current = node;
                }}
                disabled={damageType !== TireDamage.SLID_FLAT}
                required={damageType === TireDamage.SLID_FLAT}
              >
                <option key="" value="" />
                {measurementOptions}
              </select>
              <label htmlFor={`${tireKey}-measurement-1`}>MEAS. 2</label>
            </div>
            <div className="damage-input">
              <select
                className="w-100"
                name="measurement2"
                id={`${tireKey}-measurement-2`}
                value={measurement2 ?? ''}
                onChange={handleChangeMeasurement}
                ref={node => {
                  node?.toggleAttribute('blank', measurement2 === undefined);
                  measurement2Ref.current = node;
                }}
                disabled={damageType !== TireDamage.SLID_FLAT}
                required={damageType === TireDamage.SLID_FLAT}
              >
                <option value="" />
                {measurementOptions}
              </select>
              <label htmlFor={`${tireKey}-measurement-2`}>MEAS. 3</label>
            </div>
            <div className="damage-input col-span-4 row-span-2 h-100">
              <textarea
                className="h-100 w-100"
                name="notes"
                id={`${tireKey}-notes`}
                onChange={e => setCurrentTire(prevState => ({ ...prevState, notes: e.target.value }))}
                ref={node => node?.toggleAttribute('blank', !currentTire.notes)}
                value={currentTire.notes}
              />
              <label htmlFor={`${tireKey}-notes`}>NOTES</label>
            </div>
          </div>
        </div>
      </div>
    </TireDialogWrapper>
  );
};

const TireDialog: FC = () => {
  const { capture, closeDialog, dialogRef } = useTireInspections();

  return (
    <Dialog closeDialog={closeDialog} ref={dialogRef}>
      {capture ? <TireImage /> : <TireInspection />}
    </Dialog>
  );
};

export default TireDialog;
